import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { Check, Circle, ClipboardCopy, Dices, Eye, EyeOff } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { ApiError } from '~/api/errors';
import { Button } from '~/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/components/ui/form';
import { Input } from '~/components/ui/input';
import { useSetPassword } from '~/features/auth/hooks/use-set-password';
import { copyToClipboard } from '~/utils/clipboard';
import { cn } from '~/utils/cn';

// Mirrors the identity-service password rules (8–128 chars, mixed case, digit,
// special) and the confirm-match check.
const setPasswordSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, 'At least 8 characters')
      .max(128, 'Must not exceed 128 characters')
      .regex(/[A-Z]/, 'One uppercase letter')
      .regex(/[a-z]/, 'One lowercase letter')
      .regex(/\d/, 'One number')
      .regex(/[@$!%*?&]/, 'One special character (@$!%*?&)'),
    confirmPassword: z.string().min(1, 'Please confirm your new password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type SetPasswordFormValues = z.infer<typeof setPasswordSchema>;

const requirements: { label: string; test: (pw: string) => boolean }[] = [
  { label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
  { label: 'One uppercase letter', test: (pw) => /[A-Z]/.test(pw) },
  { label: 'One lowercase letter', test: (pw) => /[a-z]/.test(pw) },
  { label: 'One number', test: (pw) => /\d/.test(pw) },
  { label: 'One special character (@$!%*?&)', test: (pw) => /[@$!%*?&]/.test(pw) },
];

function getStrength(pw: string): { score: number; label: string; color: string } {
  const met = requirements.filter((r) => r.test(pw)).length;
  if (met <= 1) return { score: 0, label: '', color: '' };
  if (met === 2) return { score: 1, label: 'Weak', color: 'bg-red-500' };
  if (met === 3) return { score: 2, label: 'Fair', color: 'bg-yellow-500' };
  if (met === 4) return { score: 3, label: 'Good', color: 'bg-emerald-400' };
  return { score: 4, label: 'Strong', color: 'bg-green-600' };
}

const strengthLabelColors: Record<string, string> = {
  Weak: 'text-red-500',
  Fair: 'text-yellow-500',
  Good: 'text-emerald-500',
  Strong: 'text-green-600',
};

/** Generate a random password that satisfies every requirement. */
function generatePassword(length = 16): string {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const specials = '@$!%*?&';
  const all = upper + lower + digits + specials;
  const required = [
    upper[Math.floor(Math.random() * upper.length)],
    lower[Math.floor(Math.random() * lower.length)],
    digits[Math.floor(Math.random() * digits.length)],
    specials[Math.floor(Math.random() * specials.length)],
  ];
  const remaining = Array.from(
    { length: length - required.length },
    () => all[Math.floor(Math.random() * all.length)],
  );
  return [...required, ...remaining].sort(() => Math.random() - 0.5).join('');
}

interface SetPasswordFormProps {
  userId: string;
  token: string;
}

/**
 * Token-based set-password form: New + Confirm with strength meter, validates,
 * posts with the verification-link identifiers, then redirects to /login.
 */
export function SetPasswordForm({ userId, token }: SetPasswordFormProps) {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const mutation = useSetPassword();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [copied, setCopied] = useState(false);

  const timersNew = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timersConfirm = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Toggle a reveal flag and auto-hide it after 5s to limit shoulder-surfing.
  const toggleWithAutoHide = useCallback(
    (
      current: boolean,
      setter: (v: boolean) => void,
      timerRef: React.RefObject<ReturnType<typeof setTimeout> | null>,
    ) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (!current) {
        setter(true);
        timerRef.current = setTimeout(() => {
          setter(false);
        }, 5000);
      } else {
        setter(false);
      }
    },
    [],
  );

  const form = useForm<SetPasswordFormValues>({
    resolver: zodResolver(setPasswordSchema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const newPassword = useWatch({ control: form.control, name: 'newPassword' });
  const strength = getStrength(newPassword);

  // Fill both fields with a strong random password and briefly reveal them.
  function handleGenerate() {
    const pw = generatePassword();
    form.setValue('newPassword', pw, { shouldValidate: true, shouldDirty: true });
    form.setValue('confirmPassword', pw, { shouldValidate: true, shouldDirty: true });
    if (timersNew.current) clearTimeout(timersNew.current);
    if (timersConfirm.current) clearTimeout(timersConfirm.current);
    setShowNew(true);
    setShowConfirm(true);
    timersNew.current = setTimeout(() => {
      setShowNew(false);
    }, 5000);
    timersConfirm.current = setTimeout(() => {
      setShowConfirm(false);
    }, 5000);
  }

  function onSubmit(values: SetPasswordFormValues) {
    setSubmitError(null);
    mutation.mutate(
      { userId, token, newPassword: values.newPassword },
      {
        onSuccess: () => {
          toast.success(t('setPassword.successMessage'));
          void navigate({ to: '/login', search: { error: undefined }, replace: true });
        },
        onError: (err) => {
          const message = err instanceof ApiError ? err.message : t('setPassword.genericError');
          setSubmitError(message);
          toast.error(message);
        },
      },
    );
  }

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          void form.handleSubmit(onSubmit)(e);
        }}
        className="space-y-5"
      >
        {/* New Password */}
        <FormField
          control={form.control}
          name="newPassword"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between">
                <FormLabel>{t('setPassword.newPasswordLabel')}</FormLabel>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    className={cn(
                      'flex items-center gap-1 text-xs font-medium transition-colors',
                      copied ? 'text-green-600' : 'text-primary hover:text-primary/80',
                      newPassword ? 'cursor-pointer' : 'cursor-not-allowed',
                    )}
                    onClick={() => {
                      copyToClipboard(newPassword);
                      setCopied(true);
                      setTimeout(() => {
                        setCopied(false);
                      }, 1500);
                    }}
                    disabled={!newPassword}
                  >
                    {copied ? (
                      <Check className="h-3.5 w-3.5" />
                    ) : (
                      <ClipboardCopy className="h-3.5 w-3.5" />
                    )}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    type="button"
                    className="text-primary hover:text-primary/80 flex cursor-pointer items-center gap-1 text-xs font-medium"
                    onClick={handleGenerate}
                  >
                    <Dices className="h-3.5 w-3.5" />
                    Generate
                  </button>
                </div>
              </div>
              <FormControl>
                <div className="relative">
                  <Input
                    {...field}
                    type={showNew ? 'text' : 'password'}
                    placeholder={t('setPassword.newPasswordPlaceholder')}
                  />
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2 cursor-pointer p-1"
                    onClick={() => {
                      toggleWithAutoHide(showNew, setShowNew, timersNew);
                    }}
                  >
                    {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </FormControl>

              {/* Strength bar */}
              {newPassword.length > 0 && (
                <>
                  <div className="mt-2 flex gap-1">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className={cn(
                          'bg-muted h-1 flex-1 rounded-full transition-colors',
                          i <= strength.score && strength.color,
                        )}
                      />
                    ))}
                  </div>
                  {strength.label && (
                    <p className={cn('text-xs font-medium', strengthLabelColors[strength.label])}>
                      {strength.label}
                    </p>
                  )}
                </>
              )}

              {/* Requirements checklist */}
              <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5">
                {requirements.map((req) => {
                  const met = newPassword.length > 0 && req.test(newPassword);
                  return (
                    <li
                      key={req.label}
                      className={cn(
                        'text-muted-foreground flex items-center gap-2 text-xs',
                        met && 'text-green-600',
                      )}
                    >
                      {met ? <Check className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
                      {req.label}
                    </li>
                  );
                })}
              </ul>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Confirm Password */}
        <FormField
          control={form.control}
          name="confirmPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('setPassword.confirmPasswordLabel')}</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    {...field}
                    type={showConfirm ? 'text' : 'password'}
                    placeholder={t('setPassword.confirmPasswordPlaceholder')}
                  />
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2 cursor-pointer p-1"
                    onClick={() => {
                      toggleWithAutoHide(showConfirm, setShowConfirm, timersConfirm);
                    }}
                  >
                    {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {submitError && (
          <p role="alert" className="text-destructive text-sm">
            {submitError}
          </p>
        )}

        <Button
          type="submit"
          className="w-full cursor-pointer"
          disabled={mutation.isPending}
          isLoading={mutation.isPending}
        >
          {mutation.isPending ? t('setPassword.submitting') : t('setPassword.submitButton')}
        </Button>
      </form>
    </Form>
  );
}
